import * as React from 'react';

import { EntityDetails } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { updateBrokeredLoadAtom } from '../../_graphql/mutationBrokeredLoadUpdate';
import * as state from '../../_state/brokeredLoadDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  BrokeredLoadDetailsTab,
  BrokeredLoadFormLineItem,
  BrokeredLoadRecord,
  enumBrokeredLoadWeightType,
} from '../../types/brokeredLoad';

import { BrokeredOrderBadge, SalesOrderBadge } from './BrokeredLoadBadges';
import { BrokeredLoadStatusTransition } from './BrokeredLoadStatusTransition';
import { BrokeredLoadTabDocuments } from './tabs/BrokeredLoadTabDocuments';
import { BrokeredLoadTabMaterials } from './tabs/BrokeredLoadTabMaterials';
import { BrokeredLoadTabNotes } from './tabs/BrokeredLoadTabNotes';
import { BrokeredLoadTabPhotos } from './tabs/BrokeredLoadTabPhotos';
import { BrokeredLoadTabShipmentInfo } from './tabs/BrokeredLoadTabShipmentInfo';

import { BrokerageLayout } from '~/modules/brokerage/_components/_layouts/BrokerageLayout';
import { brokerageRoutePaths } from '~/modules/brokerage/routes';
import { LoadStatusBadge } from '~/modules/trade/_components/_shared/StatusBadges';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const mapLoadLineItemsToFormLineItems = (
  load: BrokeredLoadRecord,
): BrokeredLoadFormLineItem[] =>
  load.lineItems.map((item) => ({
    id: item.id,
    type: item.type,
    itemId: item.itemId,
    purchaseOrderId: item.purchaseOrderId ?? null,
    phase: item.phase ?? null,
    unitPrice: item.unitPrice ?? null,
    estimatedPrice: item.estimatedPrice ?? null,
    pricingUnit: item.pricingUnit ?? null,
    weights: item.weights
      ? {
          [enumBrokeredLoadWeightType.Gross]:
            item.weights[enumBrokeredLoadWeightType.Gross] ?? undefined,
          [enumBrokeredLoadWeightType.Tare]:
            item.weights[enumBrokeredLoadWeightType.Tare] ?? undefined,
          [enumBrokeredLoadWeightType.Net]:
            item.weights[enumBrokeredLoadWeightType.Net] ?? undefined,
        }
      : null,
  }));

export const BrokeredLoadDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();

  const [isSaving, setIsSaving] = React.useState(false);

  const load = useAtomValue(state.$brokeredLoadRecord);
  const { isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteBrokeredLoadDetail,
  );
  const [tab, setTab] = useAtom(state.$selectedTab);
  const [form, setForm] = useAtom(state.$brokeredLoadForm);
  const setHasUnsavedChanges = useSetAtom(state.$hasUnsavedChanges);
  const updateLoad = useSetAtom(updateBrokeredLoadAtom());

  const handleBack = React.useCallback(() => {
    navigate(brokerageRoutePaths.loads);
  }, [navigate]);

  const handleRefetch = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDiscard = React.useCallback(() => {
    if (load) {
      setForm({
        supplierId: load.supplierId ?? undefined,
        facilityId: load.facilityId ?? undefined,
        brokeredOrderId: load.brokeredOrderId ?? undefined,
        salesOrderId: load.salesOrderId ?? undefined,
        bookingId: load.bookingId ?? undefined,
        expectedShipDate: load.expectedShipDate ?? undefined,
        deliveryNumber: load.deliveryNumber ?? undefined,
        releaseNumber: load.releaseNumber ?? undefined,
        truckNumber: load.truckNumber ?? undefined,
        trailerNumber: load.trailerNumber ?? undefined,
        containerNumber: load.containerNumber ?? undefined,
        sealNumber: load.sealNumber ?? undefined,
        recyclerInternalNotes: load.recyclerInternalNotes ?? undefined,
        recyclerExternalNotes: load.recyclerExternalNotes ?? undefined,
        lineItems: mapLoadLineItemsToFormLineItems(load),
      });
      setHasUnsavedChanges(false);
      toast.success(t.brokerage.loads.messages.changesDiscarded);
    }
  }, [load, setForm, setHasUnsavedChanges, toast, t]);

  const handleSaveUpdates = React.useCallback(async () => {
    if (!load) return;

    try {
      setIsSaving(true);

      const lineItems = form.lineItems.map((item) => ({
        id: item.id.startsWith('temp-') ? undefined : item.id,
        type: item.type ?? 'Raw',
        itemId: item.itemId ?? undefined,
        purchaseOrderId: item.purchaseOrderId ?? undefined,
        phase: item.phase ?? undefined,
        unitPrice: item.unitPrice
          ? {
              amount: item.unitPrice.amount,
              currencyCode: item.unitPrice.currencyCode,
            }
          : undefined,
        estimatedPrice: item.estimatedPrice
          ? {
              amount: item.estimatedPrice.amount,
              currencyCode: item.estimatedPrice.currencyCode,
            }
          : undefined,
        pricingUnit: item.pricingUnit ?? 'lbs',
        weights: item.weights
          ? Object.fromEntries(
              Object.entries(item.weights)
                .filter(([, v]) => v !== undefined && v !== null)
                .map(([k, v]) => [k, v ? { value: v.value, unit: v.unit } : v]),
            )
          : undefined,
      }));

      await updateLoad({
        id: load.id,
        supplierId: form.supplierId ?? null,
        facilityId: form.facilityId ?? null,
        brokeredOrderId: form.brokeredOrderId ?? null,
        salesOrderId: form.salesOrderId ?? null,
        bookingId: form.bookingId ?? null,
        expectedShipDate: form.expectedShipDate,
        deliveryNumber: form.deliveryNumber ?? null,
        releaseNumber: form.releaseNumber ?? null,
        truckNumber: form.truckNumber ?? null,
        trailerNumber: form.trailerNumber ?? null,
        containerNumber: form.containerNumber ?? null,
        sealNumber: form.sealNumber ?? null,
        recyclerInternalNotes: form.recyclerInternalNotes ?? null,
        recyclerExternalNotes: form.recyclerExternalNotes ?? null,
        lineItems,
      });
      setHasUnsavedChanges(false);
      refetch();
      toast.success(t.brokerage.loads.messages.updateSuccess);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.brokerage.loads.messages.updateError,
      );
    } finally {
      setIsSaving(false);
    }
  }, [form, load, updateLoad, setHasUnsavedChanges, refetch, toast, t]);

  React.useEffect(() => {
    if (load?.timestamps?.updatedAt) {
      setForm({
        supplierId: load.supplierId ?? undefined,
        facilityId: load.facilityId ?? undefined,
        brokeredOrderId: load.brokeredOrderId ?? undefined,
        salesOrderId: load.salesOrderId ?? undefined,
        bookingId: load.bookingId ?? undefined,
        expectedShipDate: load.expectedShipDate ?? undefined,
        deliveryNumber: load.deliveryNumber ?? undefined,
        releaseNumber: load.releaseNumber ?? undefined,
        truckNumber: load.truckNumber ?? undefined,
        trailerNumber: load.trailerNumber ?? undefined,
        containerNumber: load.containerNumber ?? undefined,
        sealNumber: load.sealNumber ?? undefined,
        recyclerInternalNotes: load.recyclerInternalNotes ?? undefined,
        recyclerExternalNotes: load.recyclerExternalNotes ?? undefined,
        lineItems: mapLoadLineItemsToFormLineItems(load),
      });
      setHasUnsavedChanges(false);
    }
  }, [load?.timestamps?.updatedAt, setForm, setHasUnsavedChanges]);

  if (loadingState.isInitialLoading || isFetching) {
    return (
      <BrokerageLayout>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </BrokerageLayout>
    );
  }

  if (error || !load) {
    return (
      <BrokerageLayout>
        <div className="p-4 text-failure">
          {t.brokerage.loads.content.error(error?.message || 'Load not found')}
        </div>
      </BrokerageLayout>
    );
  }

  return (
    <BrokerageLayout className="@BrokeredLoadDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as BrokeredLoadDetailsTab)}
        currentTab={tab ?? 'load-info'}
        onBackPressed={handleBack}
        primaryTitle={load.supplier?.name ?? 'Unassigned Supplier'}
        secondaryTitle={`Load #${String(load.loadNumber).padStart(6, '0')}`}
        badges={
          <>
            {load.brokeredOrder && (
              <BrokeredOrderBadge
                orderNumber={load.brokeredOrder.orderNumber}
              />
            )}
            {load.salesOrderNumber && (
              <SalesOrderBadge orderNumber={load.salesOrderNumber} />
            )}
            <LoadStatusBadge status={load.status} isReviewed={load.reviewed} />
          </>
        }
        headerActions={
          <BrokeredLoadStatusTransition load={load} onRefetch={handleRefetch} />
        }
        tabs={{
          'load-info': {
            title: t.brokerage.loads.detail.tabs.loadInfo,
            content: () => (
              <BrokeredLoadTabShipmentInfo
                load={load}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isSaving}
              />
            ),
          },
          materials: {
            title: t.brokerage.loads.detail.tabs.materials,
            content: () => (
              <BrokeredLoadTabMaterials
                load={load}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
              />
            ),
          },
          photos: {
            title: t.brokerage.loads.detail.tabs.photos,
            content: () => <BrokeredLoadTabPhotos load={load} />,
          },
          documents: {
            title: t.brokerage.loads.detail.tabs.documents,
            content: () => <BrokeredLoadTabDocuments load={load} />,
          },
          notes: {
            title: t.brokerage.loads.detail.tabs.notes,
            content: () => <BrokeredLoadTabNotes load={load} />,
          },
        }}
      />
    </BrokerageLayout>
  );
};
